import React, { useState, useEffect, useCallback  } from 'react';
import axios from 'axios';
import withRoleAccess from '../../hoc/withRoleAccess';
import { Table, Input, Button, Modal, message, Tag, Select, Tooltip, DatePicker } from 'antd';
import { PaperClipOutlined, DeleteOutlined, ReloadOutlined, FileExcelOutlined, ClearOutlined, InfoCircleOutlined  } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { CSVLink } from 'react-csv';
import moment from 'moment';
import ReceiptModal from './ReceiptModal';
import dayjs from 'dayjs';
import './Accounting.css';

const { Search } = Input;
const { RangePicker } = DatePicker;
// Payment method mapping with color codes
const PAYMENT_METHODS = {
  'credit_card': { label: 'Credit Card', color: 'blue' },
  'cash': { label: 'Cash', color: 'green' },
  'bank_transfer': { label: 'Bank Transfer', color: 'purple' },
  'online_payment': { label: 'Online Payment', color: 'orange' }
};

const PaymentDetails = () => {
  const { t } = useTranslation();
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [filteredPaymentDetails, setFilteredPaymentDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState('');
  const [userBranchId, setUserBranchId] = useState(null);
  const [filters, setFilters] = useState({
    searchTerm: '',
    paymentMethod: null,
    bank: null,
    dateRange: {
      start: moment().startOf('month').format('YYYY-MM-DD'),
      end: moment().endOf('month').format('YYYY-MM-DD')
    }
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0
  });
  const [isReceiptModalOpen, setIsReceiptModalOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;

  // Helper function to format payment method
  const formatPaymentMethod = (method) => {
    return PAYMENT_METHODS[method]?.label || method;
  };

  // Helper function to get payment method color
  const getPaymentMethodColor = (method) => {
    return PAYMENT_METHODS[method]?.color || 'default';
  };

  useEffect(() => {
  const token = localStorage.getItem('token');
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      setUserRole(payload.role);
      setUserBranchId(payload.branch_id || payload.branchId);

      // If not superadmin, lock the branch filter to their own branch
      if (payload.role !== 'superadmin') {
        setFilters(prev => ({
          ...prev,
          branchId: payload.branch_id || payload.branchId
        }));
      }
    } catch (error) {
      console.error('Error parsing token:', error);
    }
  }
}, []);


 const fetchPaymentDetails = useCallback(async (page = 1, pageSize = 50, dateFrom = null, dateTo = null) => {
  try {
    setLoading(true);
    const token = localStorage.getItem('token');
    if (!token) return;

    const params = new URLSearchParams({
      page: page.toString(),
      limit: pageSize.toString()
    });

    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);

    // ✅ Superadmin can manually filter by branch
    if (userRole === 'superadmin' && filters.branchId) {
      params.append('branchId', filters.branchId);
    }

    const response = await axios.get(`${API_URL}/payment/allpayments?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    let responseData, paginationInfo;

    if (response.data.data && Array.isArray(response.data.data)) {
      responseData = response.data.data;
      paginationInfo = response.data.pagination;
    } else if (Array.isArray(response.data)) {
      responseData = response.data;
      paginationInfo = null;
    } else {
      console.error('Unexpected response format:', response.data);
      responseData = [];
      paginationInfo = null;
    }

    const formattedData = responseData.map(payment => ({
      ...payment,
      formatted_payment_method: formatPaymentMethod(payment.payment_method),
      class_codes_display: payment.class_items?.map(item => item.class_code).join(', ') || payment.class_code || '',
      class_count: payment.class_items?.length || (payment.class_code ? 1 : 0),
      bank_details: payment.bank_details || null,
      branch_name: payment.branch_name || "N/A",  
      branch_id: payment.branch_id || null 
    }));

    setPaymentDetails(formattedData);

    if (paginationInfo) {
      setPagination({
        current: paginationInfo.page,
        pageSize: paginationInfo.limit,
        total: paginationInfo.totalCount
      });
    } else {
      setPagination(prev => ({ ...prev, total: formattedData.length }));
    }

    setLoading(false);
  } catch (error) {
    console.error('Error fetching payment details:', error);
    setLoading(false);
  }
}, [API_URL, userRole, filters.branchId]);


  const handleDeletePayment = async (id) => {
    Modal.confirm({
      title: t('confirmDeletePayment'),
      content: t('deletePaymentWarning'),
      okText: t('yes'),
      cancelText: t('no'),
      onOk: async () => {
        try {
          const token = localStorage.getItem('token');
          await axios.delete(`${API_URL}/payment/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          message.success(t('paymentDeleted'));
          
          // Refresh with current filters
          const dateFrom = filters.dateRange.start || null;
          const dateTo = filters.dateRange.end || null;
          fetchPaymentDetails(pagination.current, pagination.pageSize, dateFrom, dateTo);
        } catch (error) {
          console.error('Error deleting payment:', error);
          message.error(t('deleteError'));
        }
      },
    });
  };

  const openReceiptModal = (record) => {
    setSelectedReceipt(record);
    setIsReceiptModalOpen(true);
  };

  const closeReceiptModal = () => {
    setIsReceiptModalOpen(false);
    setSelectedReceipt(null);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));

    // If changing date range and both dates are set, fetch data
    // (skip while a global search is active — search ignores the date range)
    if (filterType === 'dateRange') {
      if (filters.searchTerm?.trim()) {
        return;
      }
      if (value.start && value.end) {
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchPaymentDetails(1, pagination.pageSize, value.start, value.end);
      } else if (!value.start && !value.end) {
        // If both dates are cleared, fetch without date filter
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchPaymentDetails(1, pagination.pageSize);
      }
    }
  };

  const handleTableChange = (paginationInfo) => {
    const dateFrom = filters.dateRange.start || null;
    const dateTo = filters.dateRange.end || null;
    
    setPagination(paginationInfo);
    fetchPaymentDetails(paginationInfo.current, paginationInfo.pageSize, dateFrom, dateTo);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      paymentMethod: null,
      bank: null,
      dateRange: { start: '', end: '' }
    });
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchPaymentDetails(1, pagination.pageSize); // Fetch without date filters
  };

  const handleSearch = (value) => {
    handleFilterChange('searchTerm', value);
  };


  // Apply client-side filters
  useEffect(() => {
    let filtered = paymentDetails;

    // Search filter
    if (filters.searchTerm) {
      filtered = filtered.filter(payment => 
        payment.invoice_no?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        payment.firstname?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        payment.lastname?.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        payment.class_codes_display?.toLowerCase().includes(filters.searchTerm.toLowerCase())
      );
    }

    // Payment method filter
    if (filters.paymentMethod) {
      filtered = filtered.filter(payment => payment.payment_method === filters.paymentMethod);
    }

    // Bank filter
    if (filters.bank) {
      filtered = filtered.filter(payment => payment.bank_details?.bank_name === filters.bank);
    }

      if (filters.branchId) {
    filtered = filtered.filter(registration =>
      String(registration.branch_id) === String(filters.branchId)
    );
  }

    setFilteredPaymentDetails(filtered);
  }, [paymentDetails, filters.searchTerm, filters.paymentMethod, filters.bank, filters.branchId]);

  // Data load: when a search term is present, fetch across ALL dates (global,
  // large limit so the client-side search spans everything); otherwise load the
  // selected date range with normal pagination. Debounced so typing doesn't spam.
  useEffect(() => {
    const term = filters.searchTerm?.trim();
    const timer = setTimeout(() => {
      if (term) {
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchPaymentDetails(1, 100000, null, null);
      } else {
        fetchPaymentDetails(
          1,
          pagination.pageSize,
          filters.dateRange.start || null,
          filters.dateRange.end || null
        );
      }
    }, term ? 300 : 0);
    return () => clearTimeout(timer);
    // Date-range/pagination changes are handled by their own handlers; this
    // effect only reacts to the search term and branch (via fetchPaymentDetails).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.searchTerm, fetchPaymentDetails]);


    const branchColors = {
  1: "blue",
  2: "green",
  3: "magenta", 
};

  const columns = [
    {
      title: t('branch'),
      dataIndex: 'branch_name',
      key: 'branch_name',
      width: 150,
      render: (text, record) => (
        <Tag color={branchColors[record.branch_id] || "default"}>
          {text || t("N/A")}
        </Tag>
      ),
    },
      {
        title: t('recorded at'),
        dataIndex: 'timestamp',
        key: 'timestamp',
        render: (timestamp) =>
          timestamp ? moment(timestamp).format('DD MMM YYYY') : 'N/A',
        sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
        defaultSortOrder: 'descend', // 👈 newest first
      },
      {
        title: t('paid date'),
        dataIndex: 'payment_date',
        key: 'payment_date',
        render: (date) =>
          date ? moment(date).format('DD MMM YYYY') : '-',
        sorter: (a, b) => {
          if (!a.payment_date) return 1;
          if (!b.payment_date) return -1;
          return new Date(a.payment_date) - new Date(b.payment_date);
        },
      },
    {
  title: t('System Receipt No.'),
  dataIndex: 'receipt_no',
  key: 'receipt_no',
  render: (text, record) => {
    return (
      <div>
        <div>
          {text ? (
            <Button
              type="link"
              onClick={() => openReceiptModal(record)}
              style={{ padding: 0 }}
            >
              {text}
            </Button>
          ) : (
            '-'
          )}
        </div>
        {record.invoice_no && (
          <div style={{ marginTop: 4 }}>
            <Tag color="blue" size="small">
              {t('Ref.')} {record.invoice_no}
            </Tag>
          </div>
        )}
      </div>
    );
  },
},
    {
      title: t('Manual Receipt No.'),
      dataIndex: 'manual_receipt_no',
      key: 'manual_receipt_no',
      render: (text) => text || '-',
    },
    {
      title: t('student'),
      key: 'name',
      render: (_, record) => `${record.firstname || ''} ${record.lastname || ''}`.trim(),
    },
{
  title: t('Classes'),
  key: 'classes',
  render: (_, record) => (
    <div>
      {/* Class name(s) with proper wrapping */}
      <div
        style={{
          fontWeight: 'bold',
          whiteSpace: 'normal',
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
          width: '150px',
        }}
      >
        {record.class_codes_display}
      </div>

      {/* Second row: classes count + tooltip + total hours */}
      <div style={{ fontSize: '12px', color: '#666', marginTop: 2 }}>
        <Tag size="small" color={record.is_batch ? 'blue' : 'green'}>
          {record.class_count} {record.class_count === 1 ? 'class' : 'classes'}
        </Tag>

        {/* tooltip for class items */}
        {record.class_items && record.class_items.length > 0 && (
          <Tooltip
            title={
              <div>
                {record.class_items.map((item, idx) => (
                  <div key={idx}>
                    {item.class_code}: ฿{item.price?.toLocaleString()}
                  </div>
                ))}
              </div>
            }
          >
            <InfoCircleOutlined style={{ color: '#1890ff', marginLeft: 4 }} />
          </Tooltip>
        )}

        {/* total hours (if exists) */}
        {record.total_hours !== undefined && (
          <span style={{ marginLeft: 8 }}>
            {Number(record.total_hours).toFixed(1)} {t('hours')}
          </span>
        )}
      </div>
    </div>
  ),
},
  {
  title: t('paymentMethod'),
  key: 'payment_method',
  width: 200,
  render: (_, record) => {
    const wrapStyle = {
      whiteSpace: 'normal',
      overflowWrap: 'break-word',
      wordBreak: 'break-word'
    };

    const bankDetails = record.bank_details;

    return (
      <div>
        {/* Payment Method Tag */}
        <div>
          <Tag color={getPaymentMethodColor(record.payment_method)}>
            {formatPaymentMethod(record.payment_method)}
          </Tag>
        </div>

        {/* Bank Details (only for bank transfers) */}
        {record.payment_method === 'bank_transfer' && (
          <div style={{ fontSize: '12px', marginTop: 6 }}>
            {!bankDetails ? (
              <Tag color="red">{t('No Bank Info')}</Tag>
            ) : (
              <>
                <div style={{ fontWeight: 'bold', color: '#1890ff', ...wrapStyle }}>
                  {bankDetails.bank_name}
                </div>
                <div style={{ color: '#666' }}>
                  {bankDetails.account_number}
                </div>
                <div style={{ color: '#999', fontSize: '11px', ...wrapStyle }}>
                  {bankDetails.account_name}
                </div>
                {bankDetails.bank_branch && (
                  <div style={{ color: '#999', fontSize: '10px', ...wrapStyle }}>
                    {bankDetails.bank_branch}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  },
},
{
  title: t('total price'),
  dataIndex: 'total_price',
  key: 'total_price',
  render: (totalPrice) =>
    totalPrice != null
      ? `฿${Number(totalPrice).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}`
      : '฿0.00',
},

{
  title: t('paid'),
  dataIndex: 'pay_amount',
  key: 'pay_amount',
  render: (amount) => (
    <span style={{ color: 'green', fontWeight: 'bold' }}>
      ฿
      {amount != null
        ? Number(amount).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })
        : '0.00'}
    </span>
  ),
},

{
  title: t('remaining'),
  dataIndex: 'remaining_balance',
  key: 'remaining_balance',
  render: (balance) => (
    <span
      style={{
        color: balance === 0 ? 'green' : 'red',
        fontWeight: balance > 0 ? 'bold' : 'normal'
      }}
    >
      {balance > 0
        ? `(฿${Number(balance).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })})`
        : '฿0.00'}
    </span>
  ),
},
    {
      title: t('memo'),
      dataIndex: 'memo',
      key: 'memo',
      render: (memo) => (
        memo && memo.length > 20 ? (
          <Tooltip title={memo}>
            <span>{memo.substring(0, 20)}...</span>
          </Tooltip>
        ) : (memo || '-')
      ),
    },

    {
      title: t('actions'),
      key: 'actions',
      render: (_, record) => (
        <Button 
          type="primary"
          icon={<DeleteOutlined />}
          danger 
          onClick={() => handleDeletePayment(record.id)}
          size="small"
        />
      ),
    },
  ];

  // Enhanced CSV data with class information
  const csvData = filteredPaymentDetails.map((payment) => ({
    branch: payment.branch_name || "N/A",   // ✅ Add branch name
    branchId: payment.branch_id || "",
    timestamp: moment(payment.timestamp).format('DD MMM YYYY HH:mm'),
    invoiceNo: payment.invoice_no,
    receiptNo: payment.receipt_no || '',
    manualReceiptNo: payment.manual_receipt_no || '',
    firstName: payment.firstname,
    lastName: payment.lastname,
    classes: payment.class_codes_display,
    classCount: payment.class_count,
    paymentMethod: formatPaymentMethod(payment.payment_method),
    bankName: payment.bank_details?.bank_name || '',
    bankAccount: payment.bank_details?.account_number || '',
    subtotal: payment.total_price || 0,
    nettotal: payment.pay_amount || 0,
    remaining: payment.remaining_balance || 0,
    memo: payment.memo || '',
  }));

  return (
    <div className="payment-details-page">
      <div style={{ marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
        {userRole === 'superadmin' && (
                  <Select
                    placeholder={t('Select Branch')}
                    value={filters.branchId || null}
                    onChange={(val) => handleFilterChange('branchId', val)}
                    allowClear
                    style={{ width: 200 }}
                    options={[
                      ...new Map(
                        paymentDetails
                          .filter(p => p.branch_id)
                          .map(p => [p.branch_id, { label: p.branch_name, value: p.branch_id }])
                      ).values()
                    ]}
                  />
                )}
                <RangePicker
              format="DD MMM YYYY"
              value={[
                filters.dateRange.start ? dayjs(filters.dateRange.start) : null,
                filters.dateRange.end ? dayjs(filters.dateRange.end) : null,
              ]}
              onChange={(dates) => {
                handleFilterChange('dateRange', {
                  start: dates?.[0] ? dates[0].format('YYYY-MM-DD') : null,
                  end: dates?.[1] ? dates[1].format('YYYY-MM-DD') : null,
                });
              }}
              style={{ width: 260 }}
            />
           <Search
          placeholder={t('Search by invoice no., student name, or classes')}
          value={filters.searchTerm}
          onSearch={handleSearch}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />

        <Select
          placeholder={t('Select Payment Method')}
          value={filters.paymentMethod}
          onChange={(val) => handleFilterChange('paymentMethod', val)}
          allowClear
          style={{ width: 200 }}
          options={Object.entries(PAYMENT_METHODS).map(([key, { label }]) => ({
            label,
            value: key,
          }))}
        />
        
        <Select
          placeholder={t('Select Bank')}
          value={filters.bank}
          onChange={(val) => handleFilterChange('bank', val)}
          allowClear
          style={{ width: 280 }}
          options={[
            ...new Set(paymentDetails.map(p => p.bank_details?.bank_name))
          ]
            .filter(Boolean)
            .map(name => ({ label: name, value: name }))
          }
        />
        
        <Button onClick={clearFilters} icon={<ClearOutlined />}>
          {t('Clear Filter')}
        </Button>
        
        <Button 
          icon={<ReloadOutlined />} 
          onClick={() => {
            const dateFrom = filters.dateRange.start || null;
            const dateTo = filters.dateRange.end || null;
            fetchPaymentDetails(pagination.current, pagination.pageSize, dateFrom, dateTo);
          }}
        >
          {t('refresh')}
        </Button>
        
        <CSVLink 
          data={csvData} 
          filename={`payment_details_${moment().format('YYYY-MM-DD')}.csv`} 
          target="_blank"
          headers={[
             { label: 'Branch', key: 'branch' },     
            { label: 'Branch ID', key: 'branchId' },
            { label: 'Paid at', key: 'timestamp' },
            { label: 'Invoice No.', key: 'invoiceNo' },
            { label: 'Receipt No.', key: 'receiptNo' },
            { label: 'Manual Receipt No.', key: 'manualReceiptNo' },
            { label: 'First Name', key: 'firstName' },
            { label: 'Last Name', key: 'lastName' },
            { label: 'Course(s)', key: 'classes' },
            { label: 'Class Count', key: 'classCount' },
            { label: 'Payment Method', key: 'paymentMethod' },
            { label: 'Bank Name', key: 'bankName' },
            { label: 'Bank Account', key: 'bankAccount' },
            { label: 'Subtotal', key: 'subtotal' },
            { label: 'Paid', key: 'nettotal' },
            { label: 'Remaining', key: 'remaining' },
            { label: 'Memo', key: 'memo' },

          ]}
        >
          <Button icon={<FileExcelOutlined />}>
            {t('exportCSV')}
          </Button>
        </CSVLink>
      </div>

      {/* Summary Statistics */}
      <div style={{ 
        marginBottom: '16px', 
        padding: '16px', 
        backgroundColor: '#f6f6f6', 
        borderRadius: '6px',
        display: 'flex',
        gap: '24px',
        alignItems: 'center'
      }}>
        <span style={{ fontSize: 14, color: '#666' }}>
          {t('Showing')} {filteredPaymentDetails.length} 
            {(filters.searchTerm || filters.paymentMethod || filters.bank) 
              ? ` ${t('filtered from')} ${pagination.total}` 
              : ` ${t('of')} ${pagination.total}`
            } {t('payments')}
          {filters.searchTerm?.trim() ? (
            <span style={{ marginLeft: '16px', fontWeight: 'bold' }}>
              {t('Searching all dates')}
            </span>
          ) : filters.dateRange.start && filters.dateRange.end && (
            <span style={{ marginLeft: '16px', fontWeight: 'bold' }}>
              {t('Date Range')}: {moment(filters.dateRange.start).format('MMM DD, YYYY')} - {moment(filters.dateRange.end).format('MMM DD, YYYY')}
            </span>
          )}
        </span>
      </div>

      {loading ? (
        <p>{t('loading')}</p>
      ) : (
        <div className="scrollable-table">
          <Table
            dataSource={filteredPaymentDetails}
            columns={columns}
            rowKey="id"
            pagination={{
              ...pagination,
              total: (filters.searchTerm || filters.paymentMethod || filters.bank)
                ? filteredPaymentDetails.length
                : pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} payments`,
              onChange: (page, pageSize) => handleTableChange({ ...pagination, current: page, pageSize }),
              onShowSizeChange: (current, size) => handleTableChange({ ...pagination, current: 1, pageSize: size })
            }}
            onChange={(paginationInfo) => handleTableChange(paginationInfo)}
            scroll={{ x: 'max-content' }}
            sticky
          />
        </div>
      )}

      {isReceiptModalOpen && (
        <ReceiptModal 
          show={isReceiptModalOpen} 
          onClose={closeReceiptModal} 
          registration={selectedReceipt} 
        />
      )}
    </div>
  );
};

export default withRoleAccess(PaymentDetails, ['superadmin', 'admin']);